import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import service from "@assets/images/services.jpeg";
import areaImg from "@assets/images/Downtown_Dubai.webp";
import ContactSection from "../Home/contactSection";
import { motion } from "framer-motion";
import {
  FaHandshake,
  FaMapMarkedAlt,
  FaTools,
  FaComments,
  FaBuilding,
  FaSyncAlt,
} from "react-icons/fa";
import contactBg from "@assets/images/contactbg.jpg";
import { useNavigate } from "react-router-dom";
import axios from "axios";
const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;
import SEO from "../../../utils/SeoComponent";
import { ROUTES } from "../../../constants/routes";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${contactBg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  marginBottom: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const WhyChooseBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "10px",
  padding: theme.spacing(5),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  position: "relative",
  zIndex: 2,
  marginTop: "-100px",
  [theme.breakpoints.down("md")]: {
    marginTop: "-50px",
    padding: theme.spacing(3),
  },
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15)",
  },
}));

const ServiceCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 240,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "50%",
    background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
  },
}));

const ServiceCardTitle = styled(Typography)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  color: "white",
  padding: theme.spacing(2),
  zIndex: 2,
  textAlign: "center",
  fontWeight: 600,
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(4),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  backgroundColor: "#f8f9fc",
  borderRadius: "50%",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  color: "#C10E14",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
}));

const IncludedCard = styled(Card)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(4),
  borderRadius: "15px",
  backgroundColor: "#f8f9fc",
  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
}));

const ServicesPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const servicesData = [
    {
      images: areaImg,
      title: "Buy property in Dubai",
      description:
        "Find your dream home or investment property with our expert guidance through Dubai's competitive market.",
    },
    {
      images: areaImg,
      title: "Sell property in Dubai",
      description:
        "Maximize your property's value with our strategic marketing and extensive network of qualified buyers.",
    },
    {
      images: areaImg,
      title: "Rent property in Dubai",
      description:
        "Discover the perfect rental property that meets your lifestyle needs and budget requirements.",
    },
    // {
    //   images: areaImg,
    //   title: "Property Management",
    //   description:
    //     "Let us handle the complexities of property management while you enjoy consistent returns.",
    // },
  ];

  const features = [
    {
      icon: <FaHandshake />,
      title: "Professionalism & Integrity",
      description:
        "These core values are at the heart of everything we do, ensuring that each interaction is built on trust and respect.",
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Expert Local Knowledge",
      description:
        "Our agents have deep insights into Dubai's dynamic real estate market, ensuring you're always in the loop about the latest trends and opportunities.",
    },
    {
      icon: <FaTools />,
      title: "Advanced Tools & Resources",
      description:
        "Equipped with the latest technology and resources, we provide our agents with the tools they need to offer you the best service possible.",
    },
    {
      icon: <FaComments />,
      title: "Client Feedback-Driven Improvement",
      description:
        "We continuously seek and value client feedback, using it to refine our services and consistently exceed expectations.",
    },
    {
      icon: <FaBuilding />,
      title: "Affiliation with 25+ Leading Developers",
      description:
        "With strong partnerships with over 25 of Dubai's top developers, we offer you access to the most exclusive properties and investment opportunities in the city.",
    },
    {
      icon: <FaSyncAlt />,
      title: "Ongoing Adaptation & Improvement",
      description:
        "We never stop evolving, continuously adapting to changes in the market to provide you with better solutions.",
    },
  ];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${DESCO_API_BASE}/services/index`);

        setServices(res?.data?.data || []); // Check API response structure
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    };

    fetchServices();
  }, []);

  return (
    <>
      <SEO slug={"services"} />
      <Box className="services-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 }, // small screens: mt-6, medium and up: mt-14
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Our Services
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Exceptional real estate solutions tailored to your needs
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("services");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14", // Set the background color to #C10E14
                  "&:hover": {
                    backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                  },
                }}
              >
                Explore Our Services
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Why Choose DESCO Section */}
        <Container>
          <WhyChooseBox className="why-choose-section">
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <SectionTitle variant="h4" component="h2">
                    Why Choose DESCO?
                  </SectionTitle>
                  <Typography variant="body1" paragraph>
                    At DESCO, we believe that professionalism and integrity are
                    the foundation of lasting relationships. Real estate is more
                    than just a transaction to us—it's about fostering
                    meaningful connections, guiding our clients with knowledge,
                    and helping them make informed decisions at every step.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    With nearly four decades of experience, we are committed to
                    providing exceptional service and unmatched satisfaction in
                    every transaction.
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <Typography variant="body1" paragraph>
                    At DESCO, we don't just strive to meet your expectations—we
                    aim to exceed them, ensuring every experience is seamless,
                    personalized, and professional. Our commitment to quality
                    and excellence continues to define our reputation as a
                    trusted name in real estate, both locally and
                    internationally.
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: "50px",
                        py: 1,
                        px: 3,
                        backgroundColor: "white",
                        color: "#C10E14",
                        border: "2px solid #C10E14",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#C10E14",
                          color: "white",
                          boxShadow: "none",
                        },
                        "&:focus": {
                          outline: "none",
                        },
                        "&.Mui-focusVisible": {
                          outline: "none",
                        },
                      }}
                      onClick={() => {
                        const element =
                          document.getElementById("about-content");
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      Learn More About Us
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </WhyChooseBox>
        </Container>

        {/* Features Section */}
        <PageSection
          id="about-content"
          className="features-section"
          sx={{ bgcolor: "#f8f9fc" }}
        >
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                What Makes Us Different
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                Our commitment to excellence sets us apart in Dubai's
                competitive real estate market
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <FeatureItem>
                      <IconWrapper>{feature.icon}</IconWrapper>
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          fontWeight={600}
                          gutterBottom
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </FeatureItem>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection>

        {/* Our Services Section */}
        <PageSection id="services" className="services-cards-section">
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Our Services
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                Comprehensive real estate solutions tailored to meet your needs
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {services?.our_services?.map((service, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ServiceCard>
                      <Box sx={{ position: "relative" }}>
                        <ServiceCardMedia
                          image={service?.image}
                          title={service?.title}
                        />
                        <ServiceCardTitle variant="h6">
                          {service?.title}
                        </ServiceCardTitle>
                      </Box>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {service?.description}
                        </Typography>
                        <Button
                          size="small"
                          sx={{
                            mt: 2,
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: "#C10E14",
                          }}
                        >
                          Learn More
                        </Button>
                      </CardContent>
                    </ServiceCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection>

        {/* What's Included Section */}
        <PageSection
          className="whats-included-section"
          sx={{ bgcolor: "#fff" }}
        >
          <Container>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <SectionTitle variant="h4" component="h2">
                    What's Included
                  </SectionTitle>
                  <IncludedCard>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      {services?.whats_included?.map((point, index) => (
                        <Typography
                          component="li"
                          sx={{ mb: 2 }}
                          key={point.id || index}
                        >
                          {point.title}
                        </Typography>
                      ))}

                      {/* <Typography component="li" sx={{ mb: 2 }}>
                      At DESCO, we understand that every client's real estate
                      journey is unique.
                    </Typography>
                    <Typography component="li" sx={{ mb: 2 }}>
                      Whether you're buying your dream home, selling an
                      investment property, or seeking expert advice to grow your
                      portfolio.
                    </Typography>
                    <Typography component="li" sx={{ mb: 2 }}>
                      We offer a full range of services designed to meet your
                      needs at every stage.
                    </Typography>
                    <Typography component="li" sx={{ mb: 2 }}>
                      Our expert team is committed to providing personalized,
                      professional solutions.
                    </Typography>
                    <Typography component="li">
                      We focus on delivering exceptional results.
                    </Typography> */}
                    </Box>
                  </IncludedCard>
                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: "50px",
                        py: 1.5,
                        px: 4,
                        fontSize: "1rem",
                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                        backgroundColor: "#C10E14", // Set the background color to #C10E14
                        "&:hover": {
                          backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                        },
                      }}
                      onClick={() => {
                        navigate(ROUTES.CONTACT_US);
                      }}
                    >
                      Request a Consultation
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        width: "80%",
                        height: "80%",
                        bottom: "-20px",
                        right: "-20px",
                        borderRadius: "10px",
                        background: "linear-gradient(45deg, #2196F3, #3F51B5)",
                        zIndex: -1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={service}
                      alt="DESCO Services"
                      sx={{
                        width: "100%",
                        borderRadius: "10px",
                        boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </PageSection>

        {/* Contact Section */}
        <Box
          style={{
            padding: " 80px 0px ",
            backgroundImage: `url(${contactBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>
      </Box>
    </>
  );
};

export default ServicesPage;
